import { isArray, isNil, last } from 'lodash';
// Import Services
import { Helpers } from 'services';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { ComparisonUITypeEnum } from 'enums';

const { customMomentWithoutTimezoneConversion } = Helpers;

function addOpacity(color) {
    return `${color}1a`;
}

const getStaticSingleData = (
    viewType,
    { Argument: _Argument, Data, Operation: _Operation },
    additionalData,
    dateFormats,
) => {
    const format = Data.HasTime ? dateFormats.partnerDateTimeFormat : dateFormats.partnerDateFormat;
    if (Data.ValueType !== 0) {
        return viewType.replace(/\{(0|1)\}/, additionalData);
    }
    return viewType.replace(/\{(0|1)\}/, customMomentWithoutTimezoneConversion(Data.Value).format(format));
};

const getAmountSimpleFormView = (viewType, { Data }, t) => {
    const dataType = Data?.IsEquivalent ? t(l.Equivalent) : t(l.Exact);
    return viewType.replace(/\{(0|1|2)\}/, `${Data.OriginalValue} ${Data.CurrencyCode} (${dataType})`);
};

const getMonthDayFormView = (viewType, { Data }, additionalData) => {
    if (Data.ValueType !== 0) {
        return viewType.replace(/\{(0|1)\}/, additionalData);
    }
    return viewType.replace(/\{(0|1)\}/, `${additionalData?.month} ${additionalData?.day}`);
};

const getFromToView = (viewType, elementValues, t, singleViewFunc) => {
    let view = viewType;
    Object.values(elementValues).forEach((element) => {
        view = singleViewFunc(view, element, t);
    });
    return view;
};

const getFromToViewWithAdditionalData = (
    viewType,
    elementValues,
    additionalData,
    singleViewFunc,
    dateFormats: Nilable<AnyObject> = undefined,
) => {
    let view = viewType;
    Object.values(elementValues).forEach((element, i) => {
        view = singleViewFunc(view, element, additionalData ? additionalData[i] : '', dateFormats);
    });
    return view;
};

const getFormViewWithDataValue = (viewType, { Data }) => {
    return viewType.replace(/\{(0|1)\}/, isNil(Data.Value) ? 1 : Data.Value);
};

const getFormViewWithPercentageDataValue = (viewType, { Data }) => {
    return viewType.replace(/\{(0|1)\}/, `${isNil(Data.Value) ? 1 : Data.Value}%`);
};

const getFormViewWithValueLabel = (viewType, value) => {
    return viewType.replace(/\{0}/, value ? value.label : '');
};

const getMultiSelectView = (viewType, additionalData) => {
    let result = '';

    isArray(additionalData) &&
        additionalData.forEach((data, i) => {
            result += `${data?.label}` + (i !== additionalData.length - 1 ? `, ` : '');
        });
    return viewType.replace(/\{0}/, result);
};

const getTreeFormView = (viewType, elementValues) => {
    let result = '';
    const { CascadeFiltersList } = elementValues.Data;
    const data = last(CascadeFiltersList);
    isArray(data) &&
        data.forEach(({ DisplayName }, index) => {
            result += `${DisplayName}${index !== data.length - 1 ? ', ' : ''}`;
        });
    return viewType.replace(/\{0}/, result);
};

function getFiltersElementView(t, item, additionalData, dateFormats: Nilable<AnyObject> = undefined) {
    const { elementType, Value: elementValues, comparison, parent } = item ?? {};

    const viewType = t(`Comparision_${comparison?.comparison}`, {
        filterName: parent?.placeholder,
    });

    switch (elementType) {
        case ComparisonUITypeEnum.Text:
            return getFormViewWithDataValue(viewType, elementValues);
        case ComparisonUITypeEnum.DropDown: {
            return getFormViewWithValueLabel(viewType, additionalData);
        }
        case ComparisonUITypeEnum.Date:
            return getStaticSingleData(viewType, elementValues, additionalData, dateFormats);
        case ComparisonUITypeEnum.DateWithin:
            return getFromToViewWithAdditionalData(
                viewType,
                elementValues,
                additionalData,
                getStaticSingleData,
                dateFormats,
            );
        case ComparisonUITypeEnum.Number:
            return getFormViewWithDataValue(viewType, elementValues);
        case ComparisonUITypeEnum.NumberWithin:
            return getFromToView(viewType, elementValues, null, getFormViewWithDataValue);
        case ComparisonUITypeEnum.RadioButtonBox: {
            const { source } = comparison;
            const { DisplayName, Value } = JSON.parse(source).find((obj) => obj.Value === elementValues.Data.Value);

            return getFormViewWithValueLabel(viewType, {
                label: DisplayName,
                valueShouldBeSend: Value,
            });
        }
        case ComparisonUITypeEnum.Currency:
            return getAmountSimpleFormView(viewType, elementValues, t);
        case ComparisonUITypeEnum.CurrencyWithin:
            return getFromToView(viewType, elementValues, t, getAmountSimpleFormView);
        case ComparisonUITypeEnum.MonthDay:
            return getMonthDayFormView(viewType, elementValues, additionalData);
        case ComparisonUITypeEnum.MonthDayWithin:
            return getFromToViewWithAdditionalData(viewType, elementValues, additionalData, getMonthDayFormView);
        case ComparisonUITypeEnum.MultiSelect:
            return getMultiSelectView(viewType, additionalData);
        case ComparisonUITypeEnum.TreeViewWithCascadingDropdown:
            return getTreeFormView(viewType, elementValues);
        case ComparisonUITypeEnum.CascadeDataMultiCall:
            return getTreeFormView(viewType, elementValues);
        case ComparisonUITypeEnum.DateTime:
            return getStaticSingleData(viewType, elementValues, additionalData, dateFormats);
        case ComparisonUITypeEnum.DateTimeWithin:
            return getFromToViewWithAdditionalData(
                viewType,
                elementValues,
                additionalData,
                getStaticSingleData,
                dateFormats,
            );
        case ComparisonUITypeEnum.FourStepsCascadeData:
            return getTreeFormView(viewType, elementValues);
        case ComparisonUITypeEnum.FiveStepsCascadeData:
            return getTreeFormView(viewType, elementValues);
        case ComparisonUITypeEnum.Percentage:
            return getFormViewWithPercentageDataValue(viewType, elementValues);
        case ComparisonUITypeEnum.PercentageWithin:
            return getFromToView(viewType, elementValues, null, getFormViewWithPercentageDataValue);
        default:
            return viewType;
    }
}

export { addOpacity, getFiltersElementView, getFormViewWithValueLabel };
